import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { completeInfluencerProfile } from "../../../Redux_Toolkit/Auth/AuthThunk";
import { getActiveCollaborations } from "../../../Redux_Toolkit/Collaboration/CollaborationThunk";

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
const isValidPhone = (phone) => /^\d{11}$/.test(phone);
const isValidDisplayName = (name) => /^(?=.*[A-Za-z])[A-Za-z\s'-]+$/.test(name);

const getFieldValidationError = (field, value) => {
  const trimmed = (value || "").trim();

  switch (field) {
    case "displayName":
      if (!trimmed) {
        return "Display name is required.";
      }
      if (trimmed.length < 2) {
        return "Display name must be at least 2 characters.";
      }
      if (!isValidDisplayName(trimmed)) {
        return "Display name can contain letters only (no digits).";
      }
      return "";

    case "email":
      if (!trimmed) {
        return "Email is required.";
      }
      if (!isValidEmail(trimmed)) {
        return "Please enter a valid email address.";
      }
      return "";

    case "phone":
      if (!trimmed) {
        return "Phone number is required.";
      }
      if (!isValidPhone(trimmed)) {
        return "Phone number must be exactly 11 digits.";
      }
      return "";

    case "bio":
      if (trimmed.length > 300) {
        return "Bio must be 300 characters or less.";
      }
      return "";

    default:
      return "";
  }
};

function InfluencerProfileModal({ isOpen, contact, identifierType, onComplete }) {
  const dispatch = useDispatch();

  const initialValues = useMemo(() => {
    return {
      displayName: "",
      email: identifierType === "email" ? contact : "",
      phone: identifierType === "phone" ? contact : "",
      bio: "",
    };
  }, [contact, identifierType]);

  const [formData, setFormData] = useState(initialValues);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [apiError, setApiError] = useState("");

  useEffect(() => {
    setFormData(initialValues);
    setErrors({});
    setTouched({});
    setApiError("");
    setIsSubmitting(false);
  }, [initialValues]);

  if (!isOpen) {
    return null;
  }

  const normalizeFieldValue = (field, value) => {
    if (field === "displayName") {
      return value.replace(/[^a-zA-Z\s'-]/g, "").slice(0, 80);
    }

    if (field === "email") {
      return value.toLowerCase().trimStart();
    }

    if (field === "phone") {
      return value.replace(/\D/g, "").slice(0, 11);
    }

    return value;
  };

  const updateField = (field, rawValue) => {
    const value = normalizeFieldValue(field, rawValue);

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    setTouched((prev) => ({
      ...prev,
      [field]: true,
    }));

    const validationError = getFieldValidationError(field, value);
    setErrors((prev) => ({
      ...prev,
      [field]: validationError || undefined,
    }));

    setApiError("");
  };

  const handleChange = (field) => (event) => {
    updateField(field, event.target.value);
  };

  const handleBlur = (field) => () => {
    const value = formData[field];
    const validationError = getFieldValidationError(field, value);

    setTouched((prev) => ({
      ...prev,
      [field]: true,
    }));

    setErrors((prev) => ({
      ...prev,
      [field]: validationError || undefined,
    }));
  };

  const validate = () => {
    const fieldsToValidate = ["displayName", "email", "phone", "bio"];
    const nextErrors = {};

    fieldsToValidate.forEach((field) => {
      const validationError = getFieldValidationError(field, formData[field]);
      if (validationError) {
        nextErrors[field] = validationError;
      }
    });

    setErrors(nextErrors);
    setTouched({
      displayName: true,
      email: true,
      phone: true,
      bio: true,
    });

    return Object.keys(nextErrors).length === 0;
  };

  const getInputClassName = (field, isReadOnly = false) => {
    const classNames = [];
    if (isReadOnly) {
      classNames.push("readonly-field");
    }
    if (touched[field] && errors[field]) {
      classNames.push("input-error");
    }

    return classNames.join(" ");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setApiError("");

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await dispatch(
        completeInfluencerProfile({
          displayName: formData.displayName.trim(),
          email: formData.email?.trim().toLowerCase() || undefined,
          phone: formData.phone?.trim() || undefined,
          bio: formData.bio?.trim() || undefined,
        })
      ).unwrap();

      // Refresh collaboration slice so influencer home reads the new bio/displayName
      // without a manual page refresh.
      dispatch(getActiveCollaborations());

      onComplete();
    } catch (err) {
      const message =
        err?.message ||
        err?.error?.message ||
        err?.error ||
        "Unable to complete influencer profile.";
      setApiError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="profile-completion-overlay" role="dialog" aria-modal="true">
      <div className="profile-completion-card">
        <h2 className="profile-completion-title">Profile Information</h2>
        <p className="profile-completion-subtitle">
          Enter your details to complete your influencer profile.
        </p>

        <form onSubmit={handleSubmit} className="profile-completion-form">
          <div className="profile-form-group">
            <label htmlFor="influencer-display-name">Display Name</label>
            <input
              id="influencer-display-name"
              type="text"
              value={formData.displayName}
              onChange={handleChange("displayName")}
              onBlur={handleBlur("displayName")}
              placeholder="Display Name"
              className={getInputClassName("displayName")}
              maxLength={80}
            />
            {touched.displayName && errors.displayName ? (
              <span className="profile-field-error">{errors.displayName}</span>
            ) : null}
          </div>

          <div className="profile-form-group">
            <label htmlFor="influencer-email">Email</label>
            <input
              id="influencer-email"
              type="email"
              value={formData.email}
              onChange={handleChange("email")}
              onBlur={handleBlur("email")}
              placeholder="name@email.com"
              readOnly={identifierType === "email"}
              className={getInputClassName("email", identifierType === "email")}
              maxLength={100}
            />
            {touched.email && errors.email ? (
              <span className="profile-field-error">{errors.email}</span>
            ) : null}
          </div>

          <div className="profile-form-group">
            <label htmlFor="influencer-phone">Phone</label>
            <input
              id="influencer-phone"
              type="text"
              value={formData.phone}
              onChange={handleChange("phone")}
              onBlur={handleBlur("phone")}
              placeholder="Phone Number (11 digits)"
              readOnly={identifierType === "phone"}
              className={getInputClassName("phone", identifierType === "phone")}
              inputMode="numeric"
            />
            {touched.phone && errors.phone ? (
              <span className="profile-field-error">{errors.phone}</span>
            ) : null}
          </div>

          <div className="profile-form-group">
            <label htmlFor="influencer-bio">Bio</label>
            <textarea
              id="influencer-bio"
              value={formData.bio}
              onChange={handleChange("bio")}
              onBlur={handleBlur("bio")}
              placeholder="A short intro about you"
              rows={3}
              className={getInputClassName("bio")}
              maxLength={300}
            />
            {touched.bio && errors.bio ? (
              <span className="profile-field-error">{errors.bio}</span>
            ) : null}
          </div>

          {apiError ? <p className="profile-api-error">{apiError}</p> : null}

          <button type="submit" className="hybrid-primary-btn" disabled={isSubmitting}>
            {isSubmitting ? "Submitting..." : "Submit"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default InfluencerProfileModal;
